import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import { csrfToken } from "../../../../utis/csrfTokenUtils";
import { EditorInfoContext } from "../../../../utis/userInfoContext";
import { networkService } from "../../../config/axios";
import CustomTagsInput from "../../../CommonComponents/Tags/CustomTagsInput";

const TagEditorSection = ({
  docFileId,
  docId,
  docFiles,
  currentPage,
}) => {
  const { setShowToast, tagValue } = useContext(EditorInfoContext);
  const [docTags, setDocTags] = useState({});
  const [isChecked, setIsChecked] = useState({
    monitor: 0,
    all_tag: 0,
  });
  // Guard: only save checkboxes after a deliberate user click, not on data sync
  const userToggledRef = useRef(false);
  // Guard: prevent re-fetching while a save is in progress
  const isSavingRef = useRef(false);

  // Fetch fresh docFile data from server
  const fetchFreshDocFile = useCallback(async () => {
    if (!docFileId || !docId || isSavingRef.current) return;

    try {
      const { data, status } = await networkService(`docs/${docId}/files.json`, "GET");
      if (status === 200 && Array.isArray(data)) {
        const updatedDocFile = data.find((file) => file.id === docFileId);
        if (updatedDocFile) {
          setDocTags(updatedDocFile);
        }
      }
    } catch (error) {
      console.error("Error fetching fresh docFile data:", error);
    }
  }, [docFileId, docId]);

  // Save tags for the current page to the backend
  const savePageTags = useCallback(async (tagIds, type) => {
    const payload = {
      authenticity_token: csrfToken(),
      doc_id: docId,
      docs_file_id: docFileId,
      page: currentPage,
      tags_doc_files: tagIds,
    };

    isSavingRef.current = true;
    try {
      const response = await networkService(`docs/page-tags`, "POST", payload);
      if (response.status === 200) {
        if (type === "added") {
          setShowToast({
            showToast: true,
            toastType: "success",
            toastMessage: "Tag added",
          });
        }
        // Refresh from server after successful save
        await fetchFreshDocFile();
      }
    } catch (error) {
      console.error("Error saving page tags:", error);
    } finally {
      isSavingRef.current = false;
    }
  }, [docId, docFileId, currentPage, fetchFreshDocFile, setShowToast]);

  // Add a tag
  const handeAddTags = async (value) => {
    // Check for duplicates before creating
    const existingTags = docTags?.tags?.[currentPage] || [];
    const normalizedValue = value.trim().toLowerCase();
    const isDuplicate = existingTags.some(
      (tag) => (tag?.name || "").trim().toLowerCase() === normalizedValue
    );
    if (isDuplicate) {
      setShowToast({
        showToast: true,
        toastType: "danger",
        toastMessage: "This tag already exists on this page",
      });
      return;
    }

    let tagsList = await networkService(`tags.json`, "POST", { name: value });
    if (tagsList?.status === 201) {
      const newTag = tagsList.data;

      // Optimistically update local state
      setDocTags((prev) => {
        const existingPageTags = prev?.tags?.[currentPage] || [];
        // Double-check no duplicate by id
        if (existingPageTags.some((t) => t.id === newTag.id)) return prev;
        return {
          ...prev,
          tags: {
            ...(prev?.tags || {}),
            [currentPage]: [...existingPageTags, newTag],
          },
        };
      });

      // Save to backend (outside state updater)
      const currentPageTags = docTags?.tags?.[currentPage] || [];
      const allTagIds = [...currentPageTags.map((t) => t.id), newTag.id];
      // Deduplicate IDs
      const uniqueTagIds = [...new Set(allTagIds)];
      await savePageTags(uniqueTagIds, "added");
    }
  };

  // Remove a tag
  const handleRemoveTags = async (remainingTags) => {
    const remainingIds = remainingTags?.map((item) => item?.id) || [];

    // Optimistically update local state
    setDocTags((prev) => {
      const existingPageTags = prev?.tags?.[currentPage] || [];
      const filtered = existingPageTags.filter((tag) =>
        remainingIds.includes(tag.id)
      );
      return {
        ...prev,
        tags: {
          ...(prev?.tags || {}),
          [currentPage]: filtered,
        },
      };
    });

    // Save to backend
    await savePageTags(remainingIds, "deleted");
  };

  // Checkbox change handler - marks as user-initiated
  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    userToggledRef.current = true;
    setIsChecked((prev) => ({
      ...prev,
      [name]: checked ? 1 : 0,
    }));
  };

  // Only save checkbox state when the USER explicitly toggles
  useEffect(() => {
    if (!userToggledRef.current) return;
    userToggledRef.current = false;

    const saveCheckboxes = async () => {
      const payload = {
        authenticity_token: csrfToken(),
        doc_id: docId,
        docs_file_id: docFileId,
        page: currentPage,
        monitor: isChecked?.monitor,
        all_tag: isChecked?.all_tag,
      };
      try {
        await networkService(`docs/engagement-pages`, "POST", payload);
        // Refresh to get updated state from server
        await fetchFreshDocFile();
      } catch (error) {
        console.error("Error saving checkbox state:", error);
      }
    };
    saveCheckboxes();
  }, [isChecked]);

  // Initial data load - sync from props then fetch fresh
  useEffect(() => {
    if (!docFileId || !docId) return;

    const docFile = docFiles?.find((elem) => elem?.id == docFileId);
    if (docFile) {
      setDocTags(docFile);
    }
    fetchFreshDocFile();
  }, [docId, docFileId]);

  // Sync from parent prop updates (e.g. after parent refreshes docFiles)
  useEffect(() => {
    if (isSavingRef.current) return; // Don't overwrite during save
    const docFile = docFiles?.find((elem) => elem?.id == docFileId);
    if (docFile) {
      setDocTags(docFile);
    }
  }, [docFiles]);

  // Sync checkboxes from server data (without triggering a save)
  useEffect(() => {
    setIsChecked({
      monitor: docTags?.engagement_pages?.includes(currentPage) ? 1 : 0,
      all_tag: docTags?.all_page_tags?.includes(currentPage) ? 1 : 0,
    });
  }, [docTags, currentPage]);

  return (
    <div
      className={`tag-section editor-section-tags ${
        tagValue ? "move-right" : "move-left"
      }`}
    >
     <label className="mb-2 small"><b>Page Tags</b></label>
      <CustomTagsInput
        tags={docTags?.tags ? docTags?.tags[currentPage] : []}
        handleAddTags={handeAddTags}
        handleRemoveTags={handleRemoveTags}
        placeholderText="Add a page tag here"
        onShowNotification={(opts) => setShowToast(opts)}
      />
      <div className="toggle-group mt-3">
        <input
          type="checkbox"
          name="all_tag"
          value={isChecked?.all_tag}
          checked={!!isChecked?.all_tag}
          onChange={handleCheckboxChange}
        />
        <label className="ms-1 my-1">Include in engagement stats</label>
      </div>
      <div className="toggle-group">
        <input
          type="checkbox"
          name="monitor"
          value={isChecked?.monitor}
          checked={!!isChecked?.monitor}
          onChange={handleCheckboxChange}
        />
        <label className="ms-1 my-1">Apply to all language editions</label>
      </div>
    </div>
  );
};

export default TagEditorSection;
